import React, { useState, useMemo } from 'react';
import { notification } from 'tea-component';
import { useMemoizedFn, useDeepCompareEffect } from 'ahooks';
import { updateElevatorPitch } from '@gp/services/projectDetail';
import ElevatorPitchModal from './components/ElevatorPitchModal';
import ElevatorPitchContent from './components/ElevatorPitchContent';
import Loading from '@gp/components/Loading';
import { reportBtnOperationLog } from '@gp/utils/common';
import './index.scss';
import CardSlot from '@/pages/globalpedia/components/CardSlot';
import HistoryBubble from '@gp/pages/Pipeline/ProjectDetails/components/HistoryBubble';
import AutoSaveModal from '@gp/components/AutoSaveModal';
import { useAutoSaveParams, EDIT_PAGE_KEY_MAP } from '@gp/hooks/useAutoSaveParams';

export default function ElevatorPitch(props: any) {
  const {
    resData,
    loading,
    gameInformationData,
    // 是否有编辑权限
    userIsAbleEdit,
    iconList,
    refreshFun,
  } = props;

  const headTitle = 'Elevator Pitch';
  const [visible, setVisible] = useState(false);
  const [autoSaveVisible, setAutoSaveVisible] = useState(false);
  const [initialParams, setInitialParams] = useState({});
  const [valueParams, setValueParams] = useState({});

  const pageKeyCur = useMemo(() => {
    return `${EDIT_PAGE_KEY_MAP.pipeline_gameDetails_elevator_pitch}_${gameInformationData?.project_id}`;
  }, [gameInformationData]);

  useDeepCompareEffect(() => {
    const cloudPath = resData?.data?.cloud_path || '';
    const introduction = resData?.data?.introduction || '';
    const cover = resData?.data?.cover || '';
    const valueObj = {
      cloudPath,
      introduction,
      cover,
    };
    setValueParams(valueObj);
    setInitialParams(valueObj);
  }, [resData]);

  const close = useMemoizedFn(() => {
    setVisible(false);
  });

  const handleConfirm = useMemoizedFn(async (value) => {
    try {
      const { project_id: projectId, studio_entity_id: studioId } = gameInformationData;
      const params = {
        project_id: projectId,
        studio_id: studioId,
        ...value,
      } as any;

      await updateElevatorPitch(params);
      refreshFun?.();
      notification.success({
        description: 'Save success',
        duration: 1000,
      });
      deleteSaveParamsByPageKey(pageKeyCur);
      close();
    } catch (error) {
      notification.error({
        description: 'Save failed. Please check the network',
        duration: 1000,
      });
    } finally {
      reportBtnOperationLog('DETAIL_PROJECT_DETAIL_GAME_INFORMATION_EDIT_SUBMIT');
    }
  });

  const handleAutoSaveConfirm = () => {
    const saveValue = getSaveParamsValue(pageKeyCur) || [];
    setValueParams(saveValue);
    setAutoSaveVisible(false);
    setVisible(true);
  };

  const handleAutoSaveCancel = () => {
    setAutoSaveVisible(false);
    deleteSaveParamsByPageKey(pageKeyCur);
    setValueParams(initialParams);
    setVisible(true);
  };

  // 自动保存hooks
  const { getSaveParamsValue, showRestorePrompt, deleteSaveParamsByPageKey } = useAutoSaveParams({
    pageKey: pageKeyCur,
  });

  const handleEditClick = useMemoizedFn(() => {
    // 如果有自动保存过的，先咨询
    if (showRestorePrompt(pageKeyCur)) {
      setAutoSaveVisible(true);
    } else {
      setVisible(true);
    }
  });

  return (
    <>
      <CardSlot
        className="dbg-project-detail-elevator-pitch"
        title={
          <div className="dbg-project-detail_horizontal-section-title dbg-project-overview-head-title">
            {headTitle}
            <HistoryBubble loading={loading} newTime={loading ? '' : resData?.data?.update_time} />
          </div>
        }
        iconList={iconList}
        getChange={(value: any) => {
          if (value === 'pencil-m') {
            handleEditClick();
          }
        }}
      >
        {loading ? (
          <Loading loading style={{ height: 170, padding: 10 }} />
        ) : (
          <ElevatorPitchContent
            userIsAbleEdit={userIsAbleEdit}
            elevatorPitchData={resData?.data || {}}
            getAdd={() => {
              handleEditClick();
            }}
            headTitle={headTitle}
          />
        )}
      </CardSlot>

      {/* elevatorPitch 编辑弹窗 */}
      <ElevatorPitchModal
        visible={visible}
        resData={resData}
        onConfirm={handleConfirm}
        valueParams={valueParams}
        pageKeyCur={pageKeyCur}
        initialParams={initialParams}
        setValueParams={setValueParams}
        onCancel={() => {
          setValueParams(initialParams);
          deleteSaveParamsByPageKey(pageKeyCur);
          setVisible(!visible);
        }}
      />

      {/* 咨询自动保存弹窗 */}
      <AutoSaveModal
        visible={autoSaveVisible}
        onConfirm={handleAutoSaveConfirm}
        onClose={handleAutoSaveCancel}
      />
    </>
  );
}
